// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

// Part 0 (SD-T690): env-only address and key — src/config/supabaseEnv.ts is the single source.
import { SUPABASE_URL, SUPABASE_ANON_KEY as SUPABASE_PUBLISHABLE_KEY } from '@/shared/config/supabaseEnv';

// Debounce flag to prevent multiple 401 redirects from parallel queries
let isRedirecting = false;

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// Read session token from all available sources
function getSessionToken(): string {
  // 1. Window global (set by AuthContext after login)
  const globalToken = (window as any).__AGXL_SESSION_TOKEN__;
  if (globalToken) return globalToken;

  // 2. localStorage fallback (before AuthContext mounts)
  try {
    const stored = localStorage.getItem('agxl_user');
    if (stored) {
      const parsed = JSON.parse(stored);
      const token = parsed?.sessionToken;
      if (token) {
        (window as any).__AGXL_SESSION_TOKEN__ = token;
        return token;
      }
    }
  } catch { /* ignore */ }

  return '';
}

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
  },
  global: {
    headers: {
      'x-session-token': getSessionToken(),
    },
    fetch: async (url: RequestInfo | URL, options?: RequestInit) => {
      const token = getSessionToken();
      // Merge token into existing headers (handle Headers object, plain object, or array)
      const existingHeaders = options?.headers;
      let headerObj: Record<string, string> = {};

      if (existingHeaders instanceof Headers) {
        existingHeaders.forEach((v, k) => { headerObj[k] = v; });
      } else if (Array.isArray(existingHeaders)) {
        existingHeaders.forEach(([k, v]) => { headerObj[k] = v; });
      } else if (existingHeaders) {
        headerObj = { ...existingHeaders } as Record<string, string>;
      }

      if (token) {
        headerObj['x-session-token'] = token;
      }

      const response = await fetch(url, { ...options, headers: headerObj });

      // Global 401 interceptor — clear session and redirect to login.
      // 403 (PostgREST RLS rejection) is intentionally NOT treated as session-expired:
      // a logged-in farmer who hits a row-level permission failure should see a toast,
      // not be force-logged-out. RLS errors bubble up to the caller as a normal Supabase error.
      //
      // PostgREST permission errors are ALSO not session expiry: an RPC the `anon` role
      // can't EXECUTE returns 401 with code `42501`, and a SECURITY DEFINER identity guard
      // raises `42501` (forbidden) / `28000` (unauthorized). These are deploy/authorization
      // bugs (e.g. a new RPC missing `GRANT EXECUTE ... TO anon`), not an expired session —
      // force-logging-out on them is exactly the bug this guards against. Real session
      // expiry never 401s here (validate_session returns 200 with is_valid:false), so a
      // genuine auth 401 (bad/missing JWT/apikey, no PostgREST error code) still logs out.
      if (response.status === 401) {
        const urlStr = typeof url === 'string' ? url : url.toString();
        const loginTime = parseInt(localStorage.getItem('agxl_login_time') || '0');
        const isRecentLogin = Date.now() - loginTime < 10000;

        if (urlStr.includes('supabase.co') && !isRedirecting && !isRecentLogin) {
          // Read the PostgREST error code without consuming the body for the caller.
          let pgErrorCode: string | undefined;
          try {
            const errorBody = await response.clone().json();
            pgErrorCode = errorBody?.code;
          } catch {
            // Body absent or not JSON — treat as a genuine auth failure (fall through to logout).
          }

          const isPermissionError = pgErrorCode === '42501' || pgErrorCode === '28000';
          if (!isPermissionError) {
            isRedirecting = true;
            console.error('[AUTH] Session expired — redirecting to login');
            localStorage.removeItem('agxl_user');
            (window as any).__AGXL_SESSION_TOKEN__ = null;
            (window as any).__AGXL_USER_ID__ = null;
            setTimeout(() => { window.location.href = '/auth'; }, 500);
          } else {
            console.warn(`[AUTH] PostgREST permission error (${pgErrorCode}) — not a session expiry; not logging out`);
          }
        }
      }

      return response;
    }
  }
});
